
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MultiStepForm } from "@/components/MultiStepForm";
import { PricingTiers } from "@/components/PricingTiers";
import { ArrowRight, Mail, FileText, Truck, Clock, Menu, Megaphone } from "lucide-react";
import { ContactForm } from "@/components/ContactForm";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Helmet } from "react-helmet-async";

const Index = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<string | null>(null);

  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    pricingSection?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    contactSection?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  const handleGetStarted = (deliveryMethod?: string) => {
    if (deliveryMethod) {
      setSelectedDeliveryMethod(deliveryMethod);
      setDialogOpen(true);
    } else {
      scrollToPricing();
    }
  };

  const NavLinks = () => <>
      <a href="#how-it-works" className="text-[#18375d] hover:text-[#29b554]">How It Works</a>
      <a href="#pricing" className="text-[#18375d] hover:text-[#29b554]">Pricing</a>
      <a href="#contact" className="text-[#18375d] hover:text-[#29b554]">Contact</a>
    </>;

  return <div className="min-h-screen bg-background">
      <Helmet>
        <title>DesistIt! | Cease and Desist Letters Made Easy</title>
        <meta name="description" content="Send cease and desist letters with ease. Whether you need a serious legal warning or a playful notice, DesistIt! delivers AI-powered letters via email or mail." />
        <link rel="canonical" href="https://desistit.com/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "url": "https://desistit.com/",
            "name": "DesistIt!",
            "description": "Cease and desist letters made easy. AI-powered legal document generation service.",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://desistit.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "url": "https://desistit.com",
            "logo": "https://www.poweredbyeducation.org/wp-content/uploads/2025/02/DESIST2.png",
            "name": "DesistIt!",
            "description": "AI-powered cease and desist letter service",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "url": "https://desistit.com/#contact"
            },
            "sameAs": []
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Cease and Desist Letter Service",
            "provider": {
              "@type": "Organization",
              "name": "DesistIt!"
            },
            "serviceType": "Legal Document Generation",
            "description": "AI-powered cease and desist letter generation and delivery service",
            "offers": {
              "@type": "Offer",
              "price": "9.99",
              "priceCurrency": "USD"
            }
          })}
        </script>
      </Helmet>
      
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src="https://www.poweredbyeducation.org/wp-content/uploads/2025/02/DESIST2.png" alt="DesistIt! Logo" className="h-12" />
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <NavLinks />
            <Button onClick={() => handleGetStarted()} className="bg-[#29b554] hover:bg-[#29b554]/90">
              Get Started
            </Button>
          </nav>
          <Sheet>
            <SheetTrigger className="md:hidden">
              <Menu className="h-6 w-6" />
            </SheetTrigger>
            <SheetContent>
              <nav className="flex flex-col space-y-4 mt-6">
                <NavLinks />
                <Button onClick={() => handleGetStarted()} className="bg-[#29b554] hover:bg-[#29b554]/90 w-full">
                  Get Started
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main>
        <section className="container mx-auto px-4 py-20 text-center">
          <img src="https://www.poweredbyeducation.org/wp-content/uploads/2025/02/DESIST-Blog-Banner.png" alt="DesistIt! Logo" className="mx-auto mb-12 h-auto max-w-full md:max-w-3xl" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-[#18375d]">
            Cease and Desist With a Twist
          </h1>
          <p className="text-lg text-[#18375d]/80 mb-8 max-w-2xl mx-auto">
            Whether you need to send a serious warning or make a playful statement, we deliver cease and desist letters powered by AI.
          </p>
          <Button onClick={() => handleGetStarted()} className="bg-[#29b554] hover:bg-[#29b554]/90 text-lg px-8">
            Get Started <ArrowRight className="ml-2" />
          </Button>
        </section>

        <section id="how-it-works" className="bg-gray-50 py-20">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-2 text-[#18375d]">How It Works</h2>
            <p className="text-[#18375d]/80 text-center mb-12">Simple, effective, and surprisingly fun!</p>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#29b554]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Megaphone className="w-6 h-6 text-[#29b554]" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#18375d]">Air Your Grievances</h3>
                <p className="text-[#18375d]/80">Tell us a little about the struggle you're having. It can be copyright infringement, personal dispute, a playful warning, or a prank.</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-[#29b554]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-[#29b554]" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#18375d]">AI-Powered Draft</h3>
                <p className="text-[#18375d]/80">Our advanced AI creates perfectly crafted letters tailored to your needs. Proof your letter and make edits as needed before you submit it to be sent.</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-[#29b554]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Truck className="w-6 h-6 text-[#29b554]" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#18375d]">Multiple Delivery Options</h3>
                <p className="text-[#18375d]/80">Have your letter sent from our office via email, standard mail, or certified mail with tracking.</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-[#29b554]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-6 h-6 text-[#29b554]" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#18375d]">Quick & Easy</h3>
                <p className="text-[#18375d]/80">Your letter is drafted and sent in minutes. Don't delay, make your point today!</p>
              </div>
            </div>
          </div>
        </section>

        <section id="pricing" className="py-20">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-2 text-[#18375d]">Pricing</h2>
            <p className="text-[#18375d]/80 text-center mb-12">Choose your delivery method</p>
            <PricingTiers onSelect={method => handleGetStarted(method)} />
          </div>
        </section>

        <section id="contact" className="py-20">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-2 text-[#18375d]">Contact Us</h2>
            <p className="text-[#18375d]/80 text-center mb-12">Get in touch with our team</p>
            <div className="max-w-xl mx-auto">
              <ContactForm />
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-[#18375d] text-white py-12 w-full">
        <div className="mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">DesistIt!</h3>
              <p className="text-white/70">Making cease and desist letters accessible and fun for everyone.</p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/terms" className="text-white/70 hover:text-[#29b554]">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-white/70 hover:text-[#29b554]">Privacy Policy</Link></li>
                <li><Link to="/cookies" className="text-white/70 hover:text-[#29b554]">Cookie Policy</Link></li>
                <li><a href="#contact" className="text-white/70 hover:text-[#29b554]">Contact Us</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-white/70">
            © 2025 DesistIt!. All rights reserved.
          </div>
        </div>
      </footer>

      <MultiStepForm open={dialogOpen} onOpenChange={setDialogOpen} initialDeliveryMethod={selectedDeliveryMethod} />
    </div>;
};

export default Index;
